import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useTheme } from '../Theme/ThemeProvider';
import { useSelector } from 'react-redux';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { authFetch } from '../../helpers/authFetch';

function LikeButton({ productId, size = 20 }) {
  const { colors } = useTheme();
  const user = useSelector((state) => state?.user?.user);
  
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch like status and count - ONLY FOR CURRENT USER
  const fetchLikeStatus = async () => {
    if (!user?._id) {
      // User not logged in - show count but can't like
      fetchLikesCount();
      return;
    }

    try {
      // Fetch: Does CURRENT user like this product?
      const statusRes = await authFetch(
        `${summaryApi.isProductLiked?.url || 'https://josiesphones-server.onrender.com/api/likes/is-liked'}/${productId}`,
        { 
          method: 'GET'          
        }
      );

      // Fetch: Total likes from ALL users
      const countRes = await authFetch(
        `${summaryApi.getProductLikesCount?.url || 'https://josiesphones-server.onrender.com/api/likes/likes-count'}/${productId}`,
        { 
          method: 'GET'          
        }
      );

      const statusData = await statusRes.json();
      const countData = await countRes.json();

      setIsLiked(statusData.isLiked);  // CURRENT USER'S like status
      setLikesCount(countData.likesCount);  // GLOBAL like count
    } catch (error) {
      console.error('Error fetching like status:', error);
    }
  };

  // Fetch likes count only
  const fetchLikesCount = async () => {
    try {
      const countRes = await authFetch(
        `${summaryApi.getProductLikesCount?.url || 'https://josiesphones-server.onrender.com/api/likes/likes-count'}/${productId}`,
        { 
          method: 'GET'
        }
      );

      const countData = await countRes.json();
      setLikesCount(countData.likesCount);
    } catch (error) {
      console.error('Error fetching likes count:', error);
    }
  };

  // Toggle like - for CURRENT user only
  const toggleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if user is logged in
    if (!user?._id) {
      toast.warning('Please login to like products');
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLiked ? 'unlike-product' : 'like-product';
      const response = await authFetch(
        `${summaryApi.likeProduct?.url || `https://josiesphones-server.onrender.com/api/likes/${endpoint}`}/${productId}`,
        {
          method: 'POST',          
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = await response.json();

      if (data.success) {
        // Update CURRENT user's like status
        setIsLiked(!isLiked);
        // Update global count
        setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
        toast.success(isLiked ? 'Removed from likes' : 'Added to likes', { autoClose: 2000 });
      } else {
        toast.error(data.message || 'Failed to update like status');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Error updating like status');
    } finally {
      setLoading(false);
    }
  };

  // Fetch status when component mounts or productId changes
  useEffect(() => {
    fetchLikeStatus();
  }, [productId, user?._id]);

  // Show disabled state if not logged in
  const isDisabled = !user?._id;

  return (
    <button
      onClick={toggleLike}
      disabled={loading || isDisabled}
      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300 hover:scale-110 active:scale-95 disabled:opacity-50 group"
      style={{
        backgroundColor: isLiked ? `${colors.accent}20` : colors.bg,
        border: `2px solid ${colors.accent}`,
        color: colors.accent,
        cursor: isDisabled ? 'not-allowed' : 'pointer'
      }}
      title={isDisabled ? 'Login to like' : isLiked ? 'Unlike' : 'Like'}
    >
      <Heart
        size={size}
        fill={isLiked ? 'currentColor' : 'none'}
        className="transition-all group-hover:scale-110"
      />
      <span className="text-sm font-semibold">{likesCount}</span>
    </button>
  );
}

export default LikeButton;